import { useState, type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import {
  Card,
  CardContent,
  CardDescription,
  LinearSpinner,
  ToastNotification,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildGoogleAnalyticsContextMenuItems } from './context-menu-items.js';
import {
  AVAILABLE_INTEGRATIONS,
  IntegrationsEmptyCatalogState,
  IntegrationsHeaderBar,
  useWorkspaceGoogleAnalyticsSubscription,
  type IntegrationCatalogId,
  type IntegrationsSectionProps,
} from './integrations-catalog.js';
import {
  callConnectGoogleAnalyticsStreamFunction,
  callDisconnectGoogleAnalyticsIntegrationFunction,
  callSaveGoogleAnalyticsIntegrationFunction,
  callSendGoogleAnalyticsTestEventFunction,
  DEFAULT_GOOGLE_ANALYTICS_ACCOUNTS,
  findConnectedStreamMetadata,
  initiateGoogleAnalyticsOAuthAndListAccounts,
  mergeSavedGoogleAnalyticsState,
  normalizeGoogleAnalyticsState,
  resolveSelectedStreamOption,
  validateGoogleAnalyticsDraft,
  type GoogleAnalyticsAccountOption,
  type GoogleAnalyticsIntegrationState,
} from './integrations-helpers.js';
import {
  GoogleAnalyticsCardHeader,
  GoogleAnalyticsConfigModal,
  GoogleAnalyticsTileRows,
} from './integrations-modal.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';

export * from './integrations-helpers.js';
export {
  AVAILABLE_INTEGRATIONS,
  GoogleAnalyticsCardHeader,
  GoogleAnalyticsConfigModal,
  GoogleAnalyticsTileRows,
  type IntegrationCatalogId,
  type IntegrationsSectionProps,
};

export function IntegrationsSection({
  initialGoogleAnalytics,
  initialAddedIntegrations,
  googleAnalyticsAccounts = DEFAULT_GOOGLE_ANALYTICS_ACCOUNTS,
  onConnectGoogleAnalyticsOAuth,
  onConnectGoogleAnalyticsStream,
  onDisconnectGoogleAnalytics,
  onSaveGoogleAnalytics,
  onSendGoogleAnalyticsTestEvent,
}: IntegrationsSectionProps = {}): ReactElement {
  const { session, can, canSpec } = useAuth();
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  const orgId = session?.orgId ?? '';
  const workspaceId = session?.activeWorkspaceId ?? '';
  const canManage = can('domain:manage') || canSpec('configureCustomDomainsAndConnectors');
  const [gaState, setGaState] = useState<GoogleAnalyticsIntegrationState>(() =>
    normalizeGoogleAnalyticsState(initialGoogleAnalytics),
  );
  const [addedIntegrations, setAddedIntegrations] = useState<readonly IntegrationCatalogId[]>(
    () => {
      if (initialAddedIntegrations !== undefined) return initialAddedIntegrations;
      const norm = normalizeGoogleAnalyticsState(initialGoogleAnalytics);
      return norm.measurementId.length > 0 || norm.hasApiSecret ? ['google_analytics'] : [];
    },
  );
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [accounts, setAccounts] =
    useState<readonly GoogleAnalyticsAccountOption[]>(googleAnalyticsAccounts);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(
    googleAnalyticsAccounts[0]?.accountId ?? '',
  );
  const [selectedPropertyId, setSelectedPropertyId] = useState(
    googleAnalyticsAccounts[0]?.properties[0]?.propertyId ?? '',
  );
  const [selectedStreamId, setSelectedStreamId] = useState(
    googleAnalyticsAccounts[0]?.properties[0]?.streams[0]?.streamId ?? '',
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [draftEnabled, setDraftEnabled] = useState(gaState.enabled);
  const [draftMeasurementId, setDraftMeasurementId] = useState(gaState.measurementId);
  const [draftApiSecret, setDraftApiSecret] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [testingEvent, setTestingEvent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  useWorkspaceGoogleAnalyticsSubscription({
    orgId,
    workspaceId,
    skip: initialGoogleAnalytics !== undefined,
    onUpdate: (norm) => {
      setGaState(norm);
      if (norm.measurementId.length > 0 || norm.hasApiSecret) {
        setAddedIntegrations((prev) =>
          prev.includes('google_analytics') ? prev : [...prev, 'google_analytics'],
        );
      }
    },
  });

  const applySelectedStream = (
    list: readonly GoogleAnalyticsAccountOption[],
    accId: string,
    propId: string,
    strId: string,
  ): void => {
    const resolved = resolveSelectedStreamOption(list, accId, propId, strId);
    if (!resolved) return;
    setSelectedAccountId(resolved.acc.accountId);
    setSelectedPropertyId(resolved.prop.propertyId);
    setSelectedStreamId(resolved.stream.streamId);
    setDraftMeasurementId(resolved.stream.measurementId);
    if (resolved.stream.apiSecret) setDraftApiSecret(resolved.stream.apiSecret);
  };

  const handleConnectGoogleAccount = async (): Promise<void> => {
    setErrorMessage(null);
    try {
      const next = onConnectGoogleAnalyticsOAuth
        ? await onConnectGoogleAnalyticsOAuth()
        : await initiateGoogleAnalyticsOAuthAndListAccounts({ orgId, workspaceId });
      setAccounts(next);
      setGoogleConnected(true);
      applySelectedStream(next, '', '', '');
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.integrationsSaveFallback));
    }
  };

  const saveFn = onSaveGoogleAnalytics ?? callSaveGoogleAnalyticsIntegrationFunction;
  const connectStreamFn =
    onConnectGoogleAnalyticsStream ?? callConnectGoogleAnalyticsStreamFunction;
  const disconnectFn =
    onDisconnectGoogleAnalytics ?? callDisconnectGoogleAnalyticsIntegrationFunction;
  const testEventFn = onSendGoogleAnalyticsTestEvent ?? callSendGoogleAnalyticsTestEventFunction;

  const handleSave = async (e: { preventDefault: () => void }): Promise<void> => {
    e.preventDefault();
    if (!canManage) return;
    setErrorMessage(null);
    setFeedbackMessage(null);
    const matchedMeta = googleConnected
      ? findConnectedStreamMetadata(accounts, draftMeasurementId)
      : undefined;
    const allowOAuthAutoSecret = Boolean(googleConnected && matchedMeta);
    const validated = validateGoogleAnalyticsDraft(
      draftMeasurementId,
      draftApiSecret,
      gaState.hasApiSecret,
      allowOAuthAutoSecret,
    );
    if (!validated.ok) {
      setErrorMessage(validated.error);
      return;
    }
    setSubmitting(true);
    try {
      const useOAuthStreamConnect =
        allowOAuthAutoSecret &&
        matchedMeta !== undefined &&
        validated.apiSecret === undefined &&
        (onConnectGoogleAnalyticsStream !== undefined || onSaveGoogleAnalytics === undefined);
      const result = useOAuthStreamConnect
        ? await connectStreamFn({
            orgId,
            workspaceId,
            enabled: draftEnabled,
            measurementId: validated.measurementId,
            ...matchedMeta,
          })
        : await saveFn({
            orgId,
            workspaceId,
            enabled: draftEnabled,
            measurementId: validated.measurementId,
            ...(validated.apiSecret !== undefined ? { apiSecret: validated.apiSecret } : {}),
            ...(matchedMeta ?? {}),
          });
      setGaState((prev) => mergeSavedGoogleAnalyticsState(prev, result, matchedMeta));
      setDraftApiSecret('');
      setEditModalOpen(false);
      setFeedbackMessage('Google Analytics integration saved.');
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.integrationsSaveFallback));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    if (!canManage || !gaState.measurementId) return;
    setErrorMessage(null);
    setFeedbackMessage(null);
    setSubmitting(true);
    try {
      if (onDisconnectGoogleAnalytics !== undefined || onSaveGoogleAnalytics === undefined) {
        await disconnectFn({ orgId, workspaceId });
      } else {
        await saveFn({ orgId, workspaceId, enabled: false, measurementId: gaState.measurementId });
      }
      setGaState(normalizeGoogleAnalyticsState());
      setGoogleConnected(false);
      setFeedbackMessage('Google Analytics disconnected.');
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.integrationsSaveFallback));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleEnabled = async (nextEnabled: boolean): Promise<void> => {
    if (!canManage || !gaState.measurementId || !gaState.hasApiSecret) return;
    setErrorMessage(null);
    setFeedbackMessage(null);
    setSubmitting(true);
    try {
      const result = await saveFn({
        orgId,
        workspaceId,
        enabled: nextEnabled,
        measurementId: gaState.measurementId,
      });
      setGaState((prev) => ({
        ...prev,
        enabled: result.enabled,
        status: result.status,
        failureReason: result.failureReason ?? null,
      }));
      setFeedbackMessage(
        nextEnabled ? 'Google Analytics turned on.' : 'Google Analytics turned off.',
      );
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.integrationsSaveFallback));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendTestEvent = async (): Promise<void> => {
    if (!canManage || !gaState.measurementId || !gaState.hasApiSecret) return;
    setErrorMessage(null);
    setFeedbackMessage(null);
    setTestingEvent(true);
    try {
      const res = await testEventFn({ orgId, workspaceId, measurementId: gaState.measurementId });
      setGaState((prev) => ({
        ...prev,
        status: res.ok ? 'VERIFIED' : 'FAILED',
        lastTestedAt: res.testedAt,
        failureReason: res.failureReason ?? null,
      }));
      if (res.ok) {
        setFeedbackMessage(`Test event sent to Google Analytics (${gaState.measurementId}).`);
      } else {
        setErrorMessage(
          res.failureReason ??
            'Google Analytics rejected the test event. Check your Measurement ID and API secret.',
        );
      }
    } catch (err: unknown) {
      const plain = formatPlainAdminError(err, ADMIN_STRINGS.errors.integrationsTestEventFallback);
      setGaState((prev) => ({ ...prev, status: 'FAILED', failureReason: plain }));
      setErrorMessage(plain);
    } finally {
      setTestingEvent(false);
    }
  };

  const busy = submitting || testingEvent;
  const hasGaTile =
    addedIntegrations.includes('google_analytics') ||
    gaState.measurementId.length > 0 ||
    gaState.hasApiSecret;
  const openEditGaModal = (): void => {
    setDraftEnabled(gaState.enabled);
    setDraftMeasurementId(gaState.measurementId);
    setDraftApiSecret('');
    setErrorMessage(null);
    setEditModalOpen(true);
  };
  const removeGaTile = (): void =>
    setAddedIntegrations((prev) => prev.filter((id) => id !== 'google_analytics'));

  return (
    <div className="space-y-4" data-testid="integrations-card">
      <IntegrationsHeaderBar
        workspaceId={workspaceId}
        canManage={canManage}
        personaRole={session?.personaRole}
        busy={busy}
        hasGaTile={hasGaTile}
        menuOpen={addMenuOpen}
        setMenuOpen={setAddMenuOpen}
        onAddGaTile={() => {
          setAddMenuOpen(false);
          setAddedIntegrations((prev) =>
            prev.includes('google_analytics') ? prev : [...prev, 'google_analytics'],
          );
        }}
      />
      {!hasGaTile ? (
        <IntegrationsEmptyCatalogState />
      ) : (
        <div
          className="grid grid-cols-1 gap-4 xl:grid-cols-2"
          data-testid="integrations-tiles-grid"
        >
          <Card
            className="h-full"
            data-testid="integration-tile-google_analytics"
            onContextMenu={bindContextMenu(() =>
              buildGoogleAnalyticsContextMenuItems({
                gaState,
                canManageIntegrations: canManage,
                busy,
                onOpenEditModal: openEditGaModal,
                onSendTestEvent: () => void handleSendTestEvent(),
                onToggleEnabled: (next) => void handleToggleEnabled(next),
                onDisconnect: () => void handleDisconnect(),
                onRemoveTile: removeGaTile,
              }),
            )}
          >
            {busy && <LinearSpinner />}
            <GoogleAnalyticsCardHeader
              gaState={gaState}
              canManageIntegrations={canManage}
              personaRole={session?.personaRole}
              busy={busy}
              onOpenEditModal={openEditGaModal}
              onSendTestEvent={() => void handleSendTestEvent()}
              onDisconnect={() => void handleDisconnect()}
              onRemoveTile={removeGaTile}
            />
            <CardContent className="space-y-4">
              <CardDescription>
                Forward QR scan events from <span className="font-mono text-fg">{workspaceId}</span>{' '}
                to Google Analytics 4
              </CardDescription>
              <GoogleAnalyticsTileRows
                gaState={gaState}
                accounts={accounts}
                canManageIntegrations={canManage}
                submitting={submitting}
                testingEvent={testingEvent}
                onToggleEnabled={(next) => void handleToggleEnabled(next)}
              />
              <ToastNotification
                data-testid="ga-feedback-message"
                message={feedbackMessage}
                onDismiss={() => setFeedbackMessage(null)}
              />
              {errorMessage !== null && !editModalOpen && (
                <p
                  role="alert"
                  data-testid="ga-error-message"
                  className="rounded-control border border-status-error-border bg-status-error-bg p-2.5 text-[12px] font-medium text-status-error"
                >
                  {errorMessage}
                </p>
              )}
              {contextMenuElement}
            </CardContent>
          </Card>
        </div>
      )}
      <GoogleAnalyticsConfigModal
        open={editModalOpen}
        workspaceId={workspaceId}
        hasExistingSecret={gaState.hasApiSecret}
        draftEnabled={draftEnabled}
        setDraftEnabled={setDraftEnabled}
        draftMeasurementId={draftMeasurementId}
        setDraftMeasurementId={setDraftMeasurementId}
        draftApiSecret={draftApiSecret}
        setDraftApiSecret={setDraftApiSecret}
        accounts={accounts}
        googleConnected={googleConnected}
        onConnectGoogleAccount={() => void handleConnectGoogleAccount()}
        selectedAccountId={selectedAccountId}
        onSelectAccount={(accId) => applySelectedStream(accounts, accId, '', '')}
        selectedPropertyId={selectedPropertyId}
        onSelectProperty={(propId) => applySelectedStream(accounts, selectedAccountId, propId, '')}
        selectedStreamId={selectedStreamId}
        onSelectStream={(strId) =>
          applySelectedStream(accounts, selectedAccountId, selectedPropertyId, strId)
        }
        submitting={submitting}
        errorMessage={errorMessage}
        onClose={() => {
          setDraftApiSecret('');
          setEditModalOpen(false);
          setErrorMessage(null);
        }}
        onSubmit={(e) => void handleSave(e)}
      />
    </div>
  );
}
